import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import DateTimePicker, { DateTimePickerProps } from 'react-datetime-picker'
import cx from 'classnames'
import 'react-datetime-picker/dist/DateTimePicker.css'
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import DatePickerUtils, {
  DATE_PICKER_CAL_CN,
  DATE_PICKER_CAL_FIXED_CN,
  DATE_PICKER_CN,
  DATE_PICKER_SIZE_MD_CN,
  DATE_PICKER_SIZE_LG_CN,
  DATE_PICKER_TILE_CN,
} from './DatePickerUtils'
import './datePickerStyles.css'

export type DatePickerValue = Date | null
export type VariableDatePickerValue = DatePickerValue | string | undefined
export interface DatePickerCommonProps {
  size?: 'sm' | 'md' | 'lg'
  showAmPm?: boolean
  showTimeSelect?: boolean
  fixedCalendar?: boolean
  placement?: 'top' | 'bottom'
  /**
   * 날짜 입력 필드(year/month/day 등 각 세그먼트)에 숫자 외 문자 입력을 차단한다.
   * 세그먼트는 native `<input type="number">` 라 브라우저(특히 Chrome)에서 `e`/`E`/`+`/`-`/`.` 및 붙여넣기로
   * 비숫자가 입력될 수 있어, 본 옵션으로 입력 단계에서 비숫자를 막는다. (구분자 '-'/'.'는 정적 divider 라 입력 대상 아님)
   */
  numericOnly?: boolean
}

export interface DatePickerInputProps extends Omit<DateTimePickerProps, 'value' | 'onChange' | 'size'>, DatePickerCommonProps {
  value?: VariableDatePickerValue
  onChange?: (v: DatePickerValue) => void
}

const uuid = () => '_' + Math.random().toString(36).substring(2, 11)

export const DatePickerInput = forwardRef<HTMLDivElement, DatePickerInputProps>(
  (
    {
      isCalendarOpen,
      showTimeSelect,
      showAmPm,
      value,
      onChange,
      size,
      fixedCalendar,
      placement = 'bottom',
      numericOnly = false,
      className,
      calendarClassName,
      disabled = false,
      ...rest
    },
    ref,
  ) => {
    const id = useMemo(() => uuid(), [])
    const calCn = useMemo(() => `${id}__calendar`, [id])

    const [temp, setTemp] = useState<DatePickerValue>(null)
    const formattedValue = useMemo<DatePickerValue>(() => DatePickerUtils.getValidDate(value), [value])

    const showReset = useMemo(() => !!value, [value])
    const defaultOpts = useMemo(
      () => ({
        ...DatePickerUtils.getDatePickerDefaultOpts({ showTimeSelect, showAmPm, fixedCalendar }),
        calendarIcon: DatePickerUtils.getDatePickerDefaultOpts({ showTimeSelect, showAmPm, fixedCalendar }).calendarIcon,
      }),
      [showTimeSelect, showAmPm, fixedCalendar, showReset],
    )

    const handleChange = useCallback(
      (v: DatePickerValue) => {
        if (!onChange) return
        if (!dayjs(v).isValid()) return

        setTemp(v)

        // 캘린더 Day 클릭
        const $active = document.activeElement as HTMLElement
        if ($active && $active.tagName === 'BUTTON' && $active.classList.contains(DATE_PICKER_TILE_CN)) {
          onChange(v)
        }
      },
      [onChange],
    )

    const onCalendarOpen = useCallback(() => {
      if (!fixedCalendar || disabled) return
      DatePickerUtils.onFixPositionOfCal({ id, size, calCn, placement })
    }, [id, size, fixedCalendar, calCn, disabled, placement])

    const onClickInput = useCallback(() => {
      if (disabled) return
      DatePickerUtils.onForceToOpen('simple', calCn, onCalendarOpen)
      DatePickerUtils.onSelectAllOnDateInput()
    }, [calCn, disabled])

    useEffect(
      function onInitTemp() {
        if (temp === null && formattedValue) {
          setTemp(formattedValue)
        } else if (temp && !formattedValue) {
          setTemp(null)
        }
      },
      [formattedValue],
    )

    useEffect(
      function onMount() {
        if (isCalendarOpen) {
          onCalendarOpen()
        }

        const onCloseCal = () => DatePickerUtils.onForceToClose('simple', calCn)
        window.addEventListener('scroll', onCloseCal)
        return () => window.removeEventListener('scroll', onCloseCal)
      },
      [calCn],
    )

    useEffect(
      function restrictNumericInput() {
        if (!numericOnly) return

        const $wrap = document.getElementById(id)
        if (!$wrap) return

        const isSegment = (t: EventTarget | null): t is HTMLInputElement =>
          t instanceof HTMLInputElement && t.hasAttribute('data-input')

        // 1) 붙여넣기·비IME 타이핑: beforeinput 단계에서 숫자 외 문자를 차단한다.
        const onBeforeInput = (e: Event) => {
          const ie = e as InputEvent
          if (!isSegment(ie.target)) return
          // 삭제/커서 이동 등 data 가 없는 입력은 허용
          if (ie.data && /\D/.test(ie.data)) {
            ie.preventDefault()
          }
        }

        // 2) 한글(IME) 입력: beforeinput preventDefault 로는 조합이 취소되지 않으므로,
        //    조합 시작 시 해당 세그먼트를 순간 readOnly 로 토글해 조합 자체를 무효화한다.
        //    (blur 가 아니라 onBlur→onChange 부작용/포커스 이동 없음. 다음 프레임에 즉시 해제해 숫자 입력은 유지)
        const onCompositionStart = (e: Event) => {
          const target = e.target
          if (!isSegment(target)) return
          target.readOnly = true
          window.requestAnimationFrame(() => {
            target.readOnly = false
          })
        }

        $wrap.addEventListener('beforeinput', onBeforeInput)
        $wrap.addEventListener('compositionstart', onCompositionStart)
        return () => {
          $wrap.removeEventListener('beforeinput', onBeforeInput)
          $wrap.removeEventListener('compositionstart', onCompositionStart)
        }
      },
      [id, numericOnly],
    )

    return (
      <DateTimePicker
        {...defaultOpts}
        inputRef={ref}
        isCalendarOpen={isCalendarOpen}
        id={id}
        value={formattedValue}
        className={cx(
          DATE_PICKER_CN,
          {
            [DATE_PICKER_SIZE_MD_CN]: size === 'md',
            [DATE_PICKER_SIZE_LG_CN]: size === 'lg',
          },
          className,
        )}
        calendarClassName={cx(DATE_PICKER_CAL_CN, calCn, calendarClassName, { [DATE_PICKER_CAL_FIXED_CN]: fixedCalendar })}
        tileClassName={DATE_PICKER_TILE_CN}
        onChange={handleChange}
        onClick={onClickInput}
        onCalendarOpen={onCalendarOpen}
        onBlur={(e) => {
          const $target = e.target as HTMLInputElement
          if ($target && $target.tagName === 'INPUT' && onChange) onChange(temp)
        }}
        disabled={disabled}
        {...rest}
      />
    )
  },
)
